import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Microscope } from "lucide-react";
import api, { formatMoney, formatNumber, formatApiErrorDetail } from "../lib/api";
import { LabPicker, Kpi, SEGS } from "./ResumenLaboratorio";
import { CruceAbcd, StockDAnios } from "../components/labanalisis/Graficas";
import { Familias } from "../components/labanalisis/Familias";
import { PedidoPropuesto } from "../components/labanalisis/Pedido";
import { PedidosGuardados } from "../components/labanalisis/PedidosGuardados";
import { StockDevolver } from "../components/labanalisis/StockDevolver";
import { EvolucionStockVentas } from "../components/labanalisis/Evolucion";
import DrillDown from "../components/DrillDown";
import { SubirArriba } from "../components/SubirArriba";
import { NotasLaboratorio } from "../components/NotasLaboratorio";

function Kpis({ k }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4" data-testid="la-kpis">
      <Kpi label="Referencias" value={formatNumber(k.refs)} sub={`${formatNumber(k.refs_stock)} con stock · ${formatNumber(k.stock_uds)} uds en stock`} testid="la-kpi-refs" />
      <Kpi label="Stock a PVP" value={formatMoney(k.stock_pvp)} sub="Misma medida que las ventas" testid="la-kpi-stock-pvp" />
      <Kpi label="Stock a coste" value={formatMoney(k.stock_coste)} sub={`${formatNumber(k.stock_uds)} unidades`} testid="la-kpi-stock" />
      <Kpi label="Ventas (PVP)" value={formatMoney(k.ventas_eur)} sub={`${formatNumber(k.ventas_uds)} unidades vendidas`} testid="la-kpi-ventas" />
      <Kpi label="Margen bruto" value={k.margen_pct == null ? "—" : `${String(k.margen_pct).replace(".", ",")} %`} tone="text-emerald-700" sub={`${formatMoney(k.margen_eur)} sobre lo vendido`} testid="la-kpi-margen" />
      <Kpi label="Rotación" value={k.rotacion == null ? "—" : `${String(k.rotacion).replace(".", ",")}×`} sub="Ventas PVP ÷ stock PVP" testid="la-kpi-rotacion" />
      <Kpi label="Stock parado (D)" value={formatMoney(k.d_coste)} tone="text-red-600" sub={`${formatMoney(k.d_pvp)} a PVP · ${formatNumber(k.d_refs)} refs · ${formatNumber(k.d_uds)} uds`} testid="la-kpi-parado" />
    </div>
  );
}


export default function AnalisisLaboratorio() {
  const [params] = useSearchParams();
  const [labs, setLabs] = useState(null);
  const [lab, setLab] = useState(params.get("lab") || "");
  const [seg, setSeg] = useState(params.get("segment") || "all");
  const [familia, setFamilia] = useState("");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [drill, setDrill] = useState(null);
  const [abierto, setAbierto] = useState(null);
  const [recarga, setRecarga] = useState(0);

  useEffect(() => { api.get("/labs-resumen/lista", { params: { segment: seg } }).then((r) => setLabs(r.data.labs)).catch(() => setLabs([])); }, [seg]);
  useEffect(() => {
    if (!lab) return;
    setD(null); setErr("");
    api.get("/lab-analisis", { params: { laboratorio: lab, segment: seg, familia: familia || undefined } })
      .then((r) => setD(r.data)).catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo cargar el análisis."));
  }, [lab, seg, familia]);

  const elegirLab = (l) => { setLab(l); setFamilia(""); setAbierto(null); };
  const abrirGuardado = (doc) => {
    setSeg(doc.segment || "all"); setFamilia(doc.familia || ""); setAbierto({ ...doc, _n: Date.now() });
    setTimeout(() => document.querySelector("[data-testid=la-pedido]")?.scrollIntoView({ behavior: "smooth" }), 600);
  };
  const base = { laboratorio: lab, segment: seg, ...(familia ? { familia } : {}) };
  const nombre = `${lab}${familia ? ` · ${familia}` : ""}`;
  const verClase = (r) => r?.refs && setDrill({ title: `${nombre} · clase ${r.code}`, subtitle: `${formatNumber(["D", "CC"].includes(r.code) ? r.refs_stock : r.refs)} productos · ${formatMoney(r.stock_coste)} de stock a coste`, filters: { ...base, abcd: r.code } });
  const verAnio = (r) => r && setDrill({ title: `${nombre} · stock parado entrado en ${r.label}`, subtitle: `${formatNumber(r.refs)} productos sin ventas · ${formatMoney(r.coste)} a coste`, filters: { ...base, abcd: "D", con_stock: true, d_year: r.key } });

  return (
    <div className="p-4 sm:p-8 max-w-7xl fade-in" data-testid="la-page">
      <SubirArriba />
      <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold flex items-center gap-1.5"><Microscope size={12} /> Laboratorios</div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Análisis de un laboratorio</h1>
      <p className="text-slate-500 mt-2 text-sm mb-6">Elige un laboratorio: verás su stock, el cruce de ABCD de ventas y stock, el parado por años, sus familias y un pedido propuesto.</p>

      <div className="card p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="la-controls">
        <LabPicker labs={labs || []} value={lab} onPick={elegirLab} testid="la-search" />
        <div className="inline-flex bg-slate-100 border border-slate-200 rounded-lg p-0.5" data-testid="la-segment">
          {SEGS.map(([k, l]) => (
            <button key={k} onClick={() => { setSeg(k); setFamilia(""); setAbierto(null); }} data-testid={`la-seg-${k}`}
                    className={"px-3 py-1.5 text-xs font-semibold rounded-md transition-colors " + (seg === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
          ))}
        </div>
      </div>

      {!lab && <div className="card p-10 text-center text-slate-500" data-testid="la-empty">{labs ? `Busca un laboratorio para empezar (${labs.length} disponibles).` : "Cargando laboratorios…"}</div>}
      {err && <div className="card p-6 text-slate-600 mb-6" data-testid="la-error">{err}</div>}
      {lab && !d && !err && <div className="card p-6 text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Analizando {nombre}…</div>}
      {d && (
        <div className="space-y-6">
          <div className="flex items-center gap-3 flex-wrap">
            {familia && <button onClick={() => { setFamilia(""); setAbierto(null); }} className="btn btn-outline btn-sm" data-testid="la-volver-lab"><ArrowLeft size={14} /> Volver a todo {lab}</button>}
            <h2 className="font-display text-2xl font-extrabold" data-testid="la-title">{lab}{familia && <span className="text-sky-600"> · {familia}</span>}</h2>
          </div>
          <Kpis k={d.kpis} />
          <div className="grid lg:grid-cols-2 gap-6">
            <CruceAbcd rows={d.abcd} onPick={verClase} />
            <StockDAnios rows={d.d_por_anio} onPick={verAnio} />
          </div>
          <EvolucionStockVentas lab={lab} seg={seg} familia={familia} />
          {!familia && d.familias.length > 1 && <Familias key={`fam-${lab}${seg}`} rows={d.familias} onPick={setFamilia} />}
          <PedidoPropuesto key={`ped-${lab}${seg}${familia}${abierto?._n || ""}`} lab={lab} seg={seg} familia={familia} inicial={abierto} onGuardado={() => setRecarga((n) => n + 1)} />
          <PedidosGuardados lab={lab} recarga={recarga} onAbrir={abrirGuardado} />
          <StockDevolver lab={lab} seg={seg} familia={familia} />
          <NotasLaboratorio lab={lab} />
        </div>
      )}
      <DrillDown open={!!drill} onClose={() => setDrill(null)} studyId={d?.study_id} title={drill?.title} subtitle={drill?.subtitle} filters={drill?.filters || {}} />
    </div>
  );
}
